/**
 * selection.ts - Selection-related table mutation methods.
 *
 * These do not have a pure `Table -> Table` transform; their pure parts live in
 * `TableMultiSelectionLogic`. This file holds the side-effecting orchestration (store writes and
 * spreadsheet editing sync).
 */

import * as MultiSelectListUtil from 'notebook/mediocre/selection/block/MultiSelectListUtil';
import {SelectionRestoreData} from 'notebook/mediocre/selection/SelectionStore';
import {TableIndex} from 'notebook/tables/constants/TableSelectionConstants';
import * as SpreadsheetEvalUtil from 'notebook/tables/spreadsheets/SpreadsheetEvalUtil';
import type {TableMutationParams} from 'notebook/tables/store/logic/mutations/mutationParams';
import {
  computeMultiIndicesState,
  computeSelectionRestoreMetadata,
  getMultiEditingText,
  getMultiEditingTypedValue,
} from 'notebook/tables/store/logic/TableMultiSelectionLogic';

/**
 * Set the table's multi-selection anchor (`start`) and end indices.
 *
 * Writes the selection indices to the store and syncs the spreadsheet editing state for the anchor
 * cell. When `text` is omitted, the anchor cell's current text / typed value are read from the table.
 *
 * Does NOT move DOM focus — the caller is responsible for focusing the focus-bearer element
 * afterward when needed.
 *
 * @param params - The shared mutation dependencies.
 * @param indices - The `start` (anchor) and `end` indices, plus optional `text` to seed the editing
 *   state for the anchor cell.
 * @param options - Optional `multiSelIndex` to keep as the selected index; defaults to the `start`
 *   anchor when omitted.
 */
export function setMultiIndices(
  params: TableMutationParams,
  {start, end, text}: {start: TableIndex; end: TableIndex; text?: string},
  options: {multiSelIndex?: TableIndex | null} = {}
): void {
  const {table, tableNamespace, fileId, documentName} = params.getTableModel().props;

  // The selected index defaults to `start` when no explicit `multiSelIndex` is provided.
  params.setState(computeMultiIndicesState({start, end}, options.multiSelIndex ?? null));

  const editingText = text != null ? text : getMultiEditingText(table, start);
  const typedValue = getMultiEditingTypedValue(table, start);
  SpreadsheetEvalUtil.setMultiEditingForTable(
    {tableName: table.name, documentName: documentName ?? null},
    tableNamespace,
    fileId,
    {editing: false, index: start, text: editingText, typedValue, selection: null}
  );
}

/**
 * Checkpoint the current selection onto the undo/redo restore history.
 *
 * Snapshots the current multi-selection state (or an explicit override) and pushes it via the
 * table's `storeState` handler. Typically called before a structural edit so the prior selection can
 * be restored on undo.
 *
 * @param params - The shared mutation dependencies.
 * @param override - Optional selection state to snapshot instead of the current store state.
 */
export function saveSelection(
  params: TableMutationParams,
  override?: Partial<MultiSelectListUtil.MultiSelectState<TableIndex>> | {selection: SelectionRestoreData}
): void {
  const model = params.getTableModel();
  const stateToSave = override != null ? override : model.state;
  const metadata = computeSelectionRestoreMetadata(stateToSave, model.selection.multiSelectKey);
  model.props.storeState(metadata);
}
